import type { JSX } from "react";
import React, { useEffect, useRef, useState } from "react";
import { initUserStore, useIsAuthenticated, useUserActions } from "stores/user";
import { getPageObj } from "utils";
import * as Scrivito from "scrivito";
import { XCircleLoader } from "components";

/**
 * AuthGuard Component
 *
 * This component is responsible for managing user authentication state and ensuring that
 * unauthenticated users are redirected to the login page. It uses the useAuthStore hook
 * to access authentication status and performs a redirect if the user is not authenticated.
 *
 * @param {React.ReactNode} children - The application content to render when authenticated
 * @returns {JSX.Element} The children components if authenticated, or null while redirecting
 *
 * @example
 * <AuthGuard>
 *   <App />
 * </AuthGuard>
 */

export const AuthGuard = ({
  children,
}: {
  children: React.ReactNode;
}): JSX.Element => {
  const isReady = useInitAuthStore();

  if (!isReady) {
    return <XCircleLoader />;
  }

  return <AuthGuardRedirect>{children}</AuthGuardRedirect>;
};

const AuthGuardRedirect = ({ children }: { children: React.ReactNode }) => {
  const isAuthenticated = useIsAuthenticated();
  const actions = useUserActions();
  const redirectStarted = useRef(false);

  useEffect(() => {
    if (redirectStarted.current) {
      return;
    }

    const handleAuthRedirects = async () => {
      const loginPage = await Scrivito.load(() => getPageObj("LoginPage"));
      if (!loginPage) {
        console.error(
          "[AuthGuard] LoginPage not found. Please ensure a page with the path 'LoginPage' exists.",
        );

        if (!isAuthenticated) {
          window.location.href = "/user-login";
        }
        return;
      }

      const isOnLoginPage = Scrivito.currentPage()?.id() === loginPage.id();

      if (!isAuthenticated && !isOnLoginPage) {
        redirectStarted.current = true;
        await actions.redirectToLoginPage();
        return;
      }

      if (isAuthenticated && isOnLoginPage) {
        redirectStarted.current = true;
        await actions.redirectToWelcomePage();
      }
    };

    handleAuthRedirects();
  }, [isAuthenticated, actions]);

  return <>{children}</>;
};

const useInitAuthStore = () => {
  const [isReady, setIsReady] = useState(false);
  useEffect(() => {
    const executeInit = async () => {
      await initUserStore();
      setIsReady(true);
    };
    executeInit();
  }, []);
  return isReady;
};
